import type { ApiError } from "./api";

export type OpenApiContractStatus = {
  title: string;
  version: string;
  openapi: string;
  pathCount: number;
  schemaCount: number;
  requiredPaths: Array<{ path: string; label: string; present: boolean }>;
  apiRewrite: {
    status: number | null;
    contentType: string;
    json: boolean;
    htmlFallback: boolean;
  };
};

export const CONTRACT_REQUIRED_PATHS = [
  { path: "/v1/tenant-isolation/status", label: "Tenant Gate" },
  { path: "/v1/agent-runs", label: "Agent Runs" },
  { path: "/v1/agent-runs/{run_id}/replay", label: "Replay" },
  { path: "/v1/agent-runs/{run_id}/evidence-verification", label: "Evidence Gate" },
  { path: "/v1/roi/simulations", label: "ROI / Monte-Carlo" },
  { path: "/v1/compliance/fria/dossiers", label: "FRIA" },
  { path: "/v1/sources/{source_id}/quality", label: "Source Quality" },
  { path: "/v1/briefings/{briefing_id}/verification", label: "Citation Gate" },
];

type OpenApiSpec = {
  openapi?: string;
  info?: { title?: string; version?: string };
  paths?: Record<string, unknown>;
  components?: { schemas?: Record<string, unknown> };
};

export function buildOpenApiRewriteHeaders(tenantId: string | null | undefined): Headers {
  const effectiveTenantId = tenantId?.trim() ?? "";
  if (!effectiveTenantId) {
    throw { status: 401, message: "missing tenantId" } satisfies ApiError;
  }

  const headers = new Headers({ Accept: "application/json" });
  headers.set("X-Tenant-Id", effectiveTenantId);
  return headers;
}

export async function fetchOpenApiContractStatus(
  tenantId: string | null | undefined,
  fetchImpl: typeof fetch = fetch,
): Promise<OpenApiContractStatus> {
  const [specResponse, apiRewriteResponse] = await Promise.all([
    fetchImpl("/openapi.json", { cache: "no-store", headers: { Accept: "application/json" } }),
    fetchImpl("/api/v1/metric-definitions", {
      cache: "no-store",
      headers: buildOpenApiRewriteHeaders(tenantId),
    }),
  ]);

  if (!specResponse.ok) throw new Error(`openapi ${specResponse.status}`);
  const spec = (await specResponse.json()) as OpenApiSpec;

  const rewriteContentType = apiRewriteResponse.headers.get("content-type") ?? "";
  const rewriteText = await apiRewriteResponse.text();
  const paths = spec.paths ?? {};
  const schemas = spec.components?.schemas ?? {};

  return {
    title: spec.info?.title ?? "OpenAPI",
    version: spec.info?.version ?? "unknown",
    openapi: spec.openapi ?? "unknown",
    pathCount: Object.keys(paths).length,
    schemaCount: Object.keys(schemas).length,
    requiredPaths: CONTRACT_REQUIRED_PATHS.map((item) => ({
      ...item,
      present: Object.prototype.hasOwnProperty.call(paths, item.path),
    })),
    apiRewrite: {
      status: apiRewriteResponse.status,
      contentType: rewriteContentType,
      json: rewriteContentType.includes("application/json") && /^[\[{]/.test(rewriteText.trim()),
      htmlFallback: /<!doctype html|<html/i.test(rewriteText),
    },
  };
}
